import { useEffect, useState, useMemo } from 'react'
import { RefreshCw, Activity, Wifi, WifiOff } from 'lucide-react'
import { useJobStore } from '@/store/jobStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useQuery } from '@tanstack/react-query'
import { jobsApi } from '@/services/api/jobs'
import JobCard from '@/components/jobs/JobCard'
import { useTranslation } from 'react-i18next'
import { useJobsWebSocket } from '@/hooks/useJobsWebSocket'
import type { Job } from '@/types/job'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { FilterBar, ActiveFilters, FilterState, FilterConfig, applyFilters } from '@/components/ui/filters'

export default function JobQueuePage() {
  const { t } = useTranslation()
  const { jobs, loadJobs, isLoading } = useJobStore()
  const [recentJobsPage, setRecentJobsPage] = useState(1)
  const recentJobsPageSize = 10 // Fixed page size for now
  const [filterState, setFilterState] = useState<FilterState>({
    search: '',
    selectedOptions: {},
    dateRange: null,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  })

  // Filter configurations
  const filterConfigs: FilterConfig[] = [
    {
      key: 'search',
      label: 'Search',
      type: 'search',
      placeholder: 'Search jobs by script or dataset name...',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'multiselect',
      options: [
        { value: 'queued', label: 'Queued', count: jobs.filter(j => j.status === 'queued').length },
        { value: 'processing', label: 'Processing', count: jobs.filter(j => j.status === 'processing').length },
        { value: 'completed', label: 'Completed', count: jobs.filter(j => j.status === 'completed').length },
        { value: 'failed', label: 'Failed', count: jobs.filter(j => j.status === 'failed').length },
        { value: 'cancelled', label: 'Cancelled', count: jobs.filter(j => j.status === 'cancelled').length },
        { value: 'expired', label: 'Expired', count: jobs.filter(j => j.status === 'expired').length },
      ],
    },
    {
      key: 'jobType',
      label: 'Type',
      type: 'multiselect',
      options: [
        { value: 'compile', label: 'Compile', count: jobs.filter(j => j.jobType === 'compile').length },
        { value: 'execute', label: 'Execute', count: jobs.filter(j => j.jobType === 'execute').length },
        { value: 'backtest', label: 'Backtest', count: jobs.filter(j => j.jobType === 'backtest').length },
      ],
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'daterange',
    },
    {
      key: 'sort',
      label: 'Sort',
      type: 'sort',
      options: [
        { value: 'createdAt', label: 'Date Created' },
        { value: 'updatedAt', label: 'Date Updated' },
        { value: 'durationMs', label: 'Duration' },
        { value: 'status', label: 'Status' },
      ],
    },
  ]

  // Filter functions
  const filterFunctions = {
    status: (job: Job, value: string) => job.status === value,
    jobType: (job: Job, value: string) => job.jobType === value,
    dateRange: (job: Job, dateRange: any) => {
      if (!dateRange || (!dateRange.from && !dateRange.to)) return true;
      const jobDate = new Date(job.createdAt);
      if (dateRange.from && jobDate < dateRange.from) return false;
      if (dateRange.to && jobDate > dateRange.to) return false;
      return true;
    },
    sort: (a: Job, b: Job, sortBy: string) => {
      switch (sortBy) {
        case 'createdAt':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'updatedAt':
          return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        case 'durationMs':
          return (a.durationMs || 0) - (b.durationMs || 0)
        case 'status':
          const statusOrder = ['queued', 'processing', 'completed', 'failed', 'cancelled', 'expired']
          return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status)
        default:
          return 0
      }
    },
  }

  // Apply filters to all jobs
  const filteredJobs = useMemo(() => {
    return applyFilters(jobs, filterState, filterFunctions)
  }, [jobs, filterState])

  // Separate filtered active and recent jobs
  const activeJobsList = useMemo(() => {
    return filteredJobs.filter((j: Job) => j.status === 'queued' || j.status === 'processing')
  }, [filteredJobs])

  const allRecentJobs = useMemo(() => {
    return filteredJobs.filter((j: Job) => j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled' || j.status === 'expired')
  }, [filteredJobs])

  // Paginate recent jobs
  const recentJobs = useMemo(() => {
    const startIndex = (recentJobsPage - 1) * recentJobsPageSize
    const endIndex = startIndex + recentJobsPageSize
    return allRecentJobs.slice(startIndex, endIndex)
  }, [allRecentJobs, recentJobsPage, recentJobsPageSize])

  const recentJobsTotal = allRecentJobs.length
  const recentJobsTotalPages = Math.ceil(recentJobsTotal / recentJobsPageSize)

  const { data, refetch } = useQuery({
    queryKey: ['jobs', filterState],
    queryFn: async () => {
      // Build API options from current filters
      const apiOptions: any = {
        limit: 200, // Get more jobs to include both active and recent
        offset: 0,
      }

      // Add search filter
      if (filterState.search) {
        apiOptions.search = filterState.search
      }

      // Add selected options
      Object.entries(filterState.selectedOptions).forEach(([key, values]) => {
        if (values.length > 0) {
          if (key === 'status') {
            // For status, we can pass multiple values, but for simplicity use first
            apiOptions.status = values[0]
          } else if (key === 'jobType') {
            apiOptions.jobType = values[0]
          }
        }
      })

      // Add date range - format as YYYY-MM-DD for backend
      if (filterState.dateRange) {
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const startDate = filterState.dateRange.from ? formatDate(filterState.dateRange.from) : undefined;
        const endDate = filterState.dateRange.to ? formatDate(filterState.dateRange.to) : undefined;

        if (startDate) apiOptions.dateFrom = startDate;
        if (endDate) apiOptions.dateTo = endDate;
      }

      // Add sorting
      if (filterState.sortBy) {
        apiOptions.sortBy = filterState.sortBy
        apiOptions.sortOrder = filterState.sortOrder
      }

      const result = await jobsApi.list(apiOptions)
      return result.jobs
    },
    // Only refetch via polling if WebSocket is not connected
    refetchInterval: (query) => {
      const jobs = query.state.data as Job[] | undefined
      const activeJobIds = jobs
        ?.filter((j) => j.status === 'queued' || j.status === 'processing')
        .map((j) => j.id) || []
      
      // If we have active jobs, use WebSocket instead of polling
      if (activeJobIds.length > 0) {
        return false // Disable polling, use WebSocket
      }
      return 15000 // Poll every 15 seconds if no active jobs (reduced frequency)
    },
  })

  useEffect(() => {
    if (data && Array.isArray(data)) {
      // Update store with fetched data
      loadJobs()
    }
  }, [data, loadJobs])

  // Use data from query if available, otherwise fallback to store
  const jobsList = data || (Array.isArray(jobs) ? jobs : [])

  // Get active job IDs for WebSocket connection (from all jobs, not filtered)
  const activeJobIds = jobsList
    .filter((j: Job) => j.status === 'queued' || j.status === 'processing')
    .map((j: Job) => j.id)

  // Connect WebSocket to all active jobs
  const { isAnyConnected } = useJobsWebSocket(activeJobIds)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t('jobs.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('jobs.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAnyConnected ? (
            <Badge variant="default" className="gap-1">
              <Wifi className="w-3 h-3" />
              {t('jobs.live')}
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <WifiOff className="w-3 h-3" />
              {t('jobs.polling')}
            </Badge>
          )}
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      {jobs.length > 0 && (
        <div className="space-y-4">
          <FilterBar
            filters={filterConfigs}
            onChange={setFilterState}
            initialState={filterState}
          />

          <ActiveFilters
            filters={filterConfigs}
            filterState={filterState}
            onRemove={(filterKey, value) => {
              if (filterKey === 'search') {
                setFilterState(prev => ({ ...prev, search: '' }))
              } else if (filterKey === 'daterange') {
                setFilterState(prev => ({ ...prev, dateRange: null }))
              } else if (value) {
                setFilterState(prev => ({
                  ...prev,
                  selectedOptions: {
                    ...prev.selectedOptions,
                    [filterKey]: prev.selectedOptions[filterKey]?.filter(v => v !== value) || []
                  }
                }))
              }
            }}
          />

          <div className="text-sm text-muted-foreground">
            Showing {activeJobsList.length + recentJobs.length} of {filteredJobs.length} jobs
            {filteredJobs.length !== jobs.length && (
              <span> (filtered from {jobs.length} total)</span>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <LoadingPage message={t('common.loading')} />
      ) : (
        <div className="space-y-6">
          {/* Active Jobs */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5" />
              <h2 className="text-xl font-semibold">{t('jobs.activeJobs')}</h2>
              <Badge variant="secondary">{activeJobsList.length}</Badge>
              {activeJobsList.length !== jobs.filter(j => j.status === 'queued' || j.status === 'processing').length && (
                <span className="text-xs text-muted-foreground">
                  (filtered from {jobs.filter(j => j.status === 'queued' || j.status === 'processing').length})
                </span>
              )}
            </div>
            {activeJobsList.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Activity className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">{t('jobs.noActiveJobs')}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {activeJobsList.map((job: Job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            )}
          </div>

          {/* Recent Jobs */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xl font-semibold">{t('jobs.recentJobs')}</h2>
              <Badge variant="secondary">{recentJobs.length}</Badge>
              {recentJobs.length !== allRecentJobs.length && (
                <span className="text-xs text-muted-foreground">
                  (filtered from {allRecentJobs.length})
                </span>
              )}
            </div>
            {recentJobs.length === 0 && allRecentJobs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <p className="text-muted-foreground">{t('jobs.noRecentJobs')}</p>
                </CardContent>
              </Card>
            ) : recentJobs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <p className="text-muted-foreground">No recent jobs match your filters</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setFilterState({
                      search: '',
                      selectedOptions: {},
                      dateRange: null,
                      sortBy: 'createdAt',
                      sortOrder: 'desc',
                    })}
                  >
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  {recentJobs.map((job: Job) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>
                {recentJobsTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <button
                      onClick={() => setRecentJobsPage(Math.max(1, recentJobsPage - 1))}
                      disabled={recentJobsPage <= 1}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-muted-foreground">
                      Page {recentJobsPage} of {recentJobsTotalPages}
                    </span>
                    <button
                      onClick={() => setRecentJobsPage(Math.min(recentJobsTotalPages, recentJobsPage + 1))}
                      disabled={recentJobsPage >= recentJobsTotalPages}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
